import { useRef, useState, useCallback, useEffect } from 'react';
import { apiRequest } from '../../services/api';

interface MultiFileUploadProps {
  label: string;
  accept?: string;
  urls: string[];
  onUrlsChange: (urls: string[]) => void;
  onUploadingChange?: (isUploading: boolean) => void;
  helperText?: string;
  maxFileSizeMB?: number;
}

type ItemStatus = 'uploading' | 'done' | 'error';

interface UploadItem {
  id: string;
  fileName: string;
  status: ItemStatus;
  url?: string;
  error?: string;
}

const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
];

const ACCEPT_DEFAULT = 'image/*,application/pdf';

function genId() {
  return `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}

function fileIcon(fileName: string) {
  if (/\.(pdf)$/i.test(fileName)) {
    return (
      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

export default function MultiFileUpload({
  label,
  accept = ACCEPT_DEFAULT,
  urls,
  onUrlsChange,
  onUploadingChange,
  helperText,
  maxFileSizeMB = 10,
}: MultiFileUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  // Pre-populate display items for any urls that came from outside (e.g. existing records)
  // On first render we don't have filename info, so show the basename.
  const existingItems: UploadItem[] = urls.map((u) => {
    const found = items.find((it) => it.url === u && it.status === 'done');
    if (found) return found;
    return {
      id: `existing-${u}`,
      fileName: decodeURIComponent(u.split('/').pop() || u),
      status: 'done' as ItemStatus,
      url: u,
    };
  });
  const uploadingItems = items.filter((it) => it.status === 'uploading');
  const displayItems = [...uploadingItems, ...existingItems.filter(
    (ei) => !uploadingItems.some((ui) => ui.url === ei.url),
  )];

  const isBusy = items.some((it) => it.status === 'uploading');
  const prevBusyRef = useRef(false);
  useEffect(() => {
    // Only emit on actual busy-state transition so the parent counter stays accurate
    if (isBusy !== prevBusyRef.current) {
      prevBusyRef.current = isBusy;
      onUploadingChange?.(isBusy);
    }
  }, [isBusy, onUploadingChange]);

  const uploadOne = useCallback(
    async (file: File) => {
      const id = genId();
      const item: UploadItem = { id, fileName: file.name, status: 'uploading' };
      setItems((prev) => [...prev, item]);
      try {
        const formData = new FormData();
        formData.append('receiptFile', file);
        const res = await apiRequest.upload<{ url: string; message?: string }>(
          '/payments/upload-temp-receipt',
          formData,
        );
        const url = res.data?.url;
        if (!url) throw new Error('No URL returned by server');
        setItems((prev) =>
          prev.map((it) => (it.id === id ? { ...it, status: 'done', url } : it)),
        );
        onUrlsChange([...urls, url]);
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || 'Gagal memuat naik fail';
        setItems((prev) =>
          prev.map((it) => (it.id === id ? { ...it, status: 'error', error: message } : it)),
        );
      }
    },
    [urls, onUrlsChange],
  );

  const validateAndUpload = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      list.forEach((file) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
          const id = genId();
          setItems((prev) => [
            ...prev,
            { id, fileName: file.name, status: 'error', error: 'Jenis fail tidak disokong (PDF, JPG, PNG sahaja)' },
          ]);
          return;
        }
        if (file.size > maxFileSizeMB * 1024 * 1024) {
          const id = genId();
          setItems((prev) => [
            ...prev,
            { id, fileName: file.name, status: 'error', error: `Saiz fail melebihi ${maxFileSizeMB}MB` },
          ]);
          return;
        }
        uploadOne(file);
      });
      // reset input so the same file can be selected again
      if (inputRef.current) inputRef.current.value = '';
    },
    [uploadOne, maxFileSizeMB],
  );

  const handleRemove = (item: UploadItem) => {
    if (item.url) {
      onUrlsChange(urls.filter((u) => u !== item.url));
    }
    setItems((prev) => prev.filter((it) => it.id !== item.id));
  };

  const handleRetry = (item: UploadItem) => {
    // Can only retry if we still have the file — we don't keep it, so just remove the error row.
    setItems((prev) => prev.filter((it) => it.id !== item.id));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      validateAndUpload(e.dataTransfer.files);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`cursor-pointer border-2 border-dashed rounded-lg px-4 py-6 text-center transition-colors ${
          isDragging
            ? 'border-[#0D7330] bg-green-50'
            : 'border-gray-300 hover:border-[#0D7330] hover:bg-gray-50'
        }`}
      >
        <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p className="text-sm text-gray-600">
          <span className="text-[#0D7330] font-semibold">Klik untuk pilih</span> atau heret fail ke sini
        </p>
        <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, WebP (maks {maxFileSizeMB}MB)</p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(e) => e.target.files && validateAndUpload(e.target.files)}
        />
      </div>

      {helperText && <p className="text-xs text-gray-500 mt-1">{helperText}</p>}

      {displayItems.length > 0 && (
        <ul className="mt-3 space-y-2">
          {displayItems.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {fileIcon(item.fileName)}
                <span className="text-sm text-gray-700 truncate">{item.fileName}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {item.status === 'uploading' && (
                  <>
                    <span className="text-xs text-gray-500">Memuat naik...</span>
                    <svg className="animate-spin w-4 h-4 text-[#0D7330]" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </>
                )}
                {item.status === 'done' && (
                  <>
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Lihat
                      </a>
                    )}
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                )}
                {item.status === 'error' && (
                  <span className="text-xs text-red-600" title={item.error}>
                    {item.error}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => (item.status === 'error' ? handleRetry(item) : handleRemove(item))}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Buang"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
